/** Max size for any single attached file (25 MB, in bytes). */
export const MAX_FILE_BYTES = 25 * 1024 * 1024;
const MAX_FILE_MB = Math.round(MAX_FILE_BYTES / (1024 * 1024));

/** Max number of attachments per pick / per comment. */
export const MAX_FILE_COUNT = 10;

/** Whitelisted image formats. The `IMAGE_EXT_PATTERN` string and `IMAGE_MIME`
 * array are the single source of truth — adding a new image format only
 * needs touching these two. `IMAGE_EXT_RE`, `ACCEPTED_MIME`, and
 * `ACCEPTED_EXT_RE` all derive so they can't drift out of sync. */
const IMAGE_EXT_PATTERN = 'jpe?g|png|gif';
const IMAGE_MIME = ['image/jpeg', 'image/png', 'image/gif'];
const IMAGE_EXT_RE = new RegExp(`\\.(${IMAGE_EXT_PATTERN})$`, 'i');

/** Full accepted whitelist (image formats + PDF). Mirrors the file input's
 * `accept` attribute. */
export const ACCEPTED_MIME = [...IMAGE_MIME, 'application/pdf'];
const ACCEPTED_EXT_RE = new RegExp(`\\.(${IMAGE_EXT_PATTERN}|pdf)$`, 'i');

export type RejectionReason = 'type' | 'per_file_size' | 'count' | 'upload_failed';

export const REJECTION_HEADINGS: Record<RejectionReason, string> = {
    type: "File type isn't supported. Attach JPG/PNG/GIF or PDF.",
    per_file_size: `File is too large. Files must be ${MAX_FILE_MB} MB or smaller.`,
    count: `Too many attachments. You can attach up to ${MAX_FILE_COUNT} files per comment.`,
    upload_failed: 'Attachment upload failed. Please try again.',
};

export function isAcceptedType(file: File): boolean {
    // MIME or extension in the whitelist — either wins. Backend re-sniffs
    // magic bytes via `detect_upload_mimetype`, so this is a UX gate, not
    // a security boundary.
    return ACCEPTED_MIME.includes(file.type.toLowerCase()) || ACCEPTED_EXT_RE.test(file.name);
}

export function isImage(file: File): boolean {
    // Require BOTH signals to say image (or be missing). Any disagreement
    // — PDF with `.png` name, PDF with spoofed `image/*` MIME, image with
    // `application/octet-stream` MIME — falls to the file icon. A wrong
    // icon on a real image is a small UX loss; a broken `<img>` mounted
    // on PDF bytes is much worse.
    const mime = file.type.toLowerCase();
    if (mime && !IMAGE_MIME.includes(mime)) return false;
    return !file.name.includes('.') || IMAGE_EXT_RE.test(file.name);
}
